import { Badge, Box, Button, HStack, Link, Spinner, Stack, Text } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiEdit3 } from "react-icons/fi";
import { SourcingEventDto, usePreviewContractModificationNoticeQuery } from "../../../autogen/bff-api";
import { displaySum } from "../../../common/formatting/displaySum";
import { useDisplayDate } from "../../../common/formatting/useDisplayDate";
import { useApiError } from "../../../common/errors/useApiError";
import { SectionCard } from "../../sourcing-events/portal/view-single/SectionCard";
import { buildDoffinNoticeUrl } from "../doffinNoticeUrl";
import { useCanManageSourcingEvent } from "../useCanManageSourcingEvent";
import { ContractModificationModal } from "./ContractModificationModal";
import { useModificationTexts } from "./useModificationTexts";

export const ContractModificationsSection = ({ event }: { event: SourcingEventDto }) => {
  if (!event.award?.offersContractModificationNotice) return null;
  return <ContractModifications event={event} />;
};

const ContractModifications = ({ event }: { event: SourcingEventDto }) => {
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  const { show } = useApiError();
  const canManage = useCanManageSourcingEvent(event);
  const { groundLabel } = useModificationTexts();
  const [announcing, setAnnouncing] = useState(false);
  const { data: preview, isLoading, error } = usePreviewContractModificationNoticeQuery({ sourcingEventId: event.id });

  return (
    <SectionCard icon={FiEdit3} title={t("Contract modifications")} count={preview?.modifications.length}>
      <Stack spacing="3">
        {isLoading && <Spinner size="sm" />}
        {error && show(error)}
        {preview && preview.modifications.length === 0 && (
          <Text color="smMuted" fontSize="sm">
            {t("No contract modification is announced yet.")}
          </Text>
        )}
        {preview?.modifications.map((modification) => (
          <HStack
            key={modification.id}
            borderWidth="1px"
            borderColor="smBorder"
            rounded="md"
            px="4"
            py="3"
            justify="space-between"
            spacing="3"
          >
            <Box minW="0">
              <Text color="smText" fontSize="sm" fontWeight="medium">
                {groundLabel(modification.ground)}
                {modification.supplierName ? ` · ${modification.supplierName}` : ""}
              </Text>
              <Text color="smMuted" fontSize="xs">
                {modification.submittedAt ? displayDate(modification.submittedAt) : ""}
                {modification.valueAfter.value != null
                  ? ` · ${displaySum(modification.valueAfter)}`
                  : ""}
                {modification.noticeNumber ? ` · ${modification.noticeNumber}` : ""}
              </Text>
            </Box>
            <HStack spacing="3" flexShrink={0}>
              {modification.noticeNumber && (
                <Link href={buildDoffinNoticeUrl(modification.noticeNumber)} isExternal color="smPrimary" fontSize="sm">
                  {t("See on Doffin")}
                </Link>
              )}
              <Badge textTransform="none">{t(modification.status)}</Badge>
            </HStack>
          </HStack>
        ))}
        {preview && canManage && (
          <Box>
            <Button size="sm" variant="outline" colorScheme="teal" onClick={() => setAnnouncing(true)}>
              {t("Announce contract modification")}
            </Button>
          </Box>
        )}
      </Stack>
      {announcing && preview && (
        <ContractModificationModal eventId={event.id} preview={preview} onClose={() => setAnnouncing(false)} />
      )}
    </SectionCard>
  );
};
